/* Settings (Area C): profile, email change, password, 2FA, sessions, activity, notification preferences,
 * and the organisation's general details, logo and deletion. */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { authToken } from '@/api/token'
import type { Organization } from '@/api/types'
import type {
  DeviceSession,
  NotificationPreference,
  NotificationPreferencesBody,
  OrganizationGeneralBody,
  OrganizationProfile,
  Profile,
  ProfileBody,
  RecoveryCodes,
  SecurityEvent,
  TwoFactorSetup,
} from '@/api/types/settings'
import { i18n, setLocale } from '@/i18n'
import type { Locale } from '@/i18n/locale'
import { useLocale } from '@/i18n/useLocale'
import { authKeys, useSession } from './auth'
import { meKeys } from './me'
import { organizationKeys } from './organization'
import { teamKeys } from './team'

export const settingsKeys = {
  profile: ['settings', 'profile'] as const,
  sessions: (locale: string) => ['settings', 'sessions', locale] as const,
  activity: (locale: string) => ['settings', 'activity', locale] as const,
  prefs: ['settings', 'notification-preferences'] as const,
  orgProfile: ['settings', 'organization-profile'] as const,
}

export const profileQuery = () =>
  queryOptions({
    queryKey: settingsKeys.profile,
    queryFn: ({ signal }) => api.get<Profile>('/me/profile', { signal }),
  })

export function useProfile() {
  return useQuery(profileQuery())
}

function useRefreshMe() {
  const qc = useQueryClient()
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: settingsKeys.profile }),
      qc.invalidateQueries({ queryKey: meKeys.me }),
      qc.invalidateQueries({ queryKey: authKeys.session }),
      qc.invalidateQueries({ queryKey: organizationKeys.members }),
      qc.invalidateQueries({ queryKey: ['settings', 'activity'] }),
      qc.invalidateQueries({ queryKey: teamKeys.security }),
    ])
}

export function useUpdateProfile() {
  const refresh = useRefreshMe()
  return useMutation({
    mutationFn: (body: ProfileBody) => api.patch<Profile>('/me/profile', body),
    onSuccess: refresh,
    meta: { silent: true },
  })
}

/**
 * Switches the interface language now and, when signed in, saves it as the profile language (so emails
 * and the next sign-in follow it). Used by the top bar switch, the user menu and the command palette.
 */
export function useSwitchLanguage() {
  const { data: session } = useSession()
  const qc = useQueryClient()
  const save = useMutation({
    mutationFn: (locale: Locale) => api.patch<Profile>('/me/profile', { locale }),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: settingsKeys.profile }),
        qc.invalidateQueries({ queryKey: authKeys.session }),
      ]),
    meta: { silent: true },
  })
  return (locale: Locale) => {
    setLocale(locale)
    if (session && session.user.locale !== locale) save.mutate(locale)
  }
}

export function useChangeEmail() {
  const refresh = useRefreshMe()
  return useMutation({
    mutationFn: (body: { email: string; password: string }) => api.post<Profile>('/me/email', body),
    onSuccess: refresh,
    meta: { silent: true },
  })
}

export function useConfirmEmail() {
  const refresh = useRefreshMe()
  return useMutation({
    mutationFn: (token: string) => api.post<Profile>('/me/email/confirm', { token }),
    onSuccess: refresh,
    meta: { success: i18n.t('settings:profile.email.confirmed') },
  })
}

export function useCancelEmailChange() {
  const refresh = useRefreshMe()
  return useMutation({
    mutationFn: () => api.delete('/me/email'),
    onSuccess: refresh,
  })
}

export function useChangePassword() {
  const qc = useQueryClient()
  const refresh = useRefreshMe()
  return useMutation({
    mutationFn: (body: { currentPassword: string; newPassword: string }) => api.post('/me/password', body),
    onSuccess: () => Promise.all([refresh(), qc.invalidateQueries({ queryKey: ['settings', 'sessions'] })]),
    meta: { silent: true },
  })
}

export function useStartTwoFactor() {
  return useMutation({
    mutationFn: () => api.post<TwoFactorSetup>('/me/2fa/setup'),
  })
}

export function useEnableTwoFactor() {
  const refresh = useRefreshMe()
  return useMutation({
    mutationFn: (code: string) => api.post<RecoveryCodes>('/me/2fa/enable', { code }),
    onSuccess: refresh,
    meta: { silent: true },
  })
}

export function useDisableTwoFactor() {
  const refresh = useRefreshMe()
  return useMutation({
    mutationFn: (password: string) => api.post('/me/2fa/disable', { password }),
    onSuccess: refresh,
    meta: { silent: true },
  })
}

export function useRegenerateRecoveryCodes() {
  const refresh = useRefreshMe()
  return useMutation({
    mutationFn: (password: string) => api.post<RecoveryCodes>('/me/2fa/recovery-codes', { password }),
    onSuccess: refresh,
    meta: { silent: true },
  })
}

export function useSessions() {
  const { locale } = useLocale()
  return useQuery({
    queryKey: settingsKeys.sessions(locale),
    queryFn: ({ signal }) => api.get<DeviceSession[]>('/me/sessions', { signal }),
  })
}

export function useRevokeSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/me/sessions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['settings', 'sessions'] }),
    meta: { success: i18n.t('settings:security.sessions.revoked') },
  })
}

export function useRevokeOtherSessions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.post('/me/sessions/revoke-others'),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ['settings', 'sessions'] }),
        qc.invalidateQueries({ queryKey: ['settings', 'activity'] }),
      ]),
    meta: { success: i18n.t('settings:security.sessions.revokedAll') },
  })
}

export function useSecurityActivity() {
  const { locale } = useLocale()
  return useQuery({
    queryKey: settingsKeys.activity(locale),
    queryFn: ({ signal }) => api.get<SecurityEvent[]>('/me/activity', { signal }),
  })
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: settingsKeys.prefs,
    queryFn: ({ signal }) => api.get<NotificationPreference[]>('/me/notification-preferences', { signal }),
  })
}

export function useSaveNotificationPreferences() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: NotificationPreferencesBody) =>
      api.put<NotificationPreference[]>('/me/notification-preferences', body),
    onSuccess: (data) => qc.setQueryData(settingsKeys.prefs, data),
    meta: { success: i18n.t('settings:notifications.saved') },
  })
}

/* ---------- organisation ---------- */

export function useOrganizationProfile() {
  return useQuery({
    queryKey: settingsKeys.orgProfile,
    queryFn: ({ signal }) => api.get<OrganizationProfile>('/organization/profile', { signal }),
  })
}

export function useUpdateOrganization() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: OrganizationGeneralBody) => api.patch<Organization>('/organization', body),
    onSuccess: (org) => {
      qc.setQueryData(organizationKeys.detail, org)
      return qc.invalidateQueries({ queryKey: authKeys.session })
    },
    meta: { silent: true },
  })
}

export function useUploadLogo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dataUrl: string) => api.put<{ logoUrl: string }>('/organization/logo', { dataUrl }),
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.orgProfile }),
    meta: { success: i18n.t('settings:organization.logo.saved') },
  })
}

export function useRemoveLogo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete('/organization/logo'),
    onSuccess: () => qc.invalidateQueries({ queryKey: settingsKeys.orgProfile }),
  })
}

export function useDeleteOrganization() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (confirmName: string) => api.delete('/organization', { query: { confirmName } }),
    onSuccess: () => {
      authToken.clear()
      qc.clear()
      qc.setQueryData(authKeys.session, null)
    },
  })
}
